import { defineStore } from "pinia";
import { useDark } from "@vueuse/core";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";

const isDarkMode = useDark({
  selector: "html",
  attribute: "data-theme",
  valueLight: "",
  valueDark: "dark"
});

type SettingStateFlags = {
  enforceUsNumberFormatting: boolean;
  openPumpTableByDefault: boolean;
  openSideCalcsByDefault: boolean;
  minimumPumpChartFrequency: number;
  maximumPumpChartFrequency: number;
  displayHpsOnly: boolean;
};

type SettingsState = {
  activeCalculatorSections: string[];
  currentScenarioSideCalcsOpen: boolean;
  currentScenarioChartsOpen: boolean;
  flags: SettingStateFlags;
  sideCalcState: string[];
  scenarioAnalysisMode: "indiscrete" | "discrete";
};

export const useSettingsStore = defineStore({
  id: "settings",
  state: (): SettingsState => ({
    sideCalcState: [EquipmentCalculatorSectionKey.PUMP],
    activeCalculatorSections: [],
    currentScenarioSideCalcsOpen: false,
    currentScenarioChartsOpen: false,
    scenarioAnalysisMode: "indiscrete",
    flags: {
      enforceUsNumberFormatting: false,
      openPumpTableByDefault: false,
      openSideCalcsByDefault: false,
      minimumPumpChartFrequency: 45,
      maximumPumpChartFrequency: 65,
      displayHpsOnly: false
    }
  }),
  getters: {
    isDarkMode() {
      return isDarkMode.value;
    },
    userSettingsNumberFormat(): string {
      return this.flags.enforceUsNumberFormatting
        ? "en-US"
        : navigator.language; // Set the default locale to the user's browser language
    }
  },
  actions: {
    toggleFlag<K extends keyof SettingStateFlags>(setting: K) {
      const newSetting = !this.flags[setting];
      this.flags = {
        ...this.flags,
        [setting]: newSetting
      };
    },
    removeFromActiveCalculatorSections(value: EquipmentCalculatorSectionKey) {
      this.activeCalculatorSections = this.activeCalculatorSections.filter(
        i => i !== value
      );
    },
    pushToActiveCalculatorSections(value: EquipmentCalculatorSectionKey) {
      if (!this.activeCalculatorSections.includes(value)) {
        this.activeCalculatorSections = [
          ...this.activeCalculatorSections,
          value
        ];
      }
    },
    setActiveCalculatorSections(value: EquipmentCalculatorSectionKey[]) {
      this.activeCalculatorSections = value;
    },
    toggleDarkMode(toggle?: boolean) {
      isDarkMode.value = toggle !== undefined ? toggle : !isDarkMode.value;
    },
    setMinimumPumpChartFrequency(value: number) {
      this.flags.minimumPumpChartFrequency = value;
    },
    setMaximumPumpChartFrequency(value: number) {
      this.flags.maximumPumpChartFrequency = value;
    },
    setActiveSideCalcState(value: EquipmentCalculatorSectionKey[]) {
      this.sideCalcState = value;
    },
    setScenarioAnalysisMode(value: "discrete" | "indiscrete") {
      this.scenarioAnalysisMode = value;
    }
  },
  persist: true
});
